import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ActionsButton, Datatable } from 'ui';
import { LinkButton } from 'ui/Buttons';
import { useSelectedTableRows, useTable } from 'shared/hooks';
import { SelectedEntities, ArtifactId } from 'shared/types/portfolios';
import { tableRefreshReasonTypes } from 'shared/constants/tableRefreshReasonTypes';
import { MUIDataTableMeta } from 'mui-datatables';
import { usePortfolioModals } from 'shared/hooks/usePortfolioModals/usePortfolioModals';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { theme } from 'theme';
import { useFetchPortfolioRatingNews, useDeleteRatingNews } from '../../GraphQL/portfolios/hooks';
import { UpdateEntitiesModal } from '../../Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { CreateNewPortfolioModal } from '../../Modals/CreateNewPortfolioModal';
import { makeRatingNewsTableData } from '../utils';
import { PortfolioItemsProps, PortfolioAction, PortfolioType } from '../types';

const useStyles = makeStyles(() => ({
  root: {
    '& thead > tr': {
      whiteSpace: 'pre',
    },
  },
  buttonLink: {
    cursor: 'pointer',
  },
}));

export const PortfolioRatingNews: FC<PortfolioItemsProps> = ({
  portfolioId,
  total = 0,
  refetchPortfolio = null,
  portfolioData,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    singleSelect,
    multiSelect,
    currentSelect,
    selectedAll,
    onSelectAll,
    onSelectedChange,
    selectSingle,
  } = useSelectedTableRows();

  const [updating, setUpdating] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: '' });
  const { error, success } = useSnackbar();
  const [variant, setVariant] = useState<PortfolioAction>();
  const { createPortfolio, addToPortfolio } = usePortfolioModals();
  const deleteRatineNews = useDeleteRatingNews();

  const { isDedicated } = portfolioData;

  /* ******************************************************************** */
  /*                            Event handlers                            */
  /* ******************************************************************** */

  const deleteEntity = () => {
    setUpdating(true);

    const items = Object.keys(currentSelect.itemIds).map((item: string) => {
      return {
        value: item,
        type: currentSelect.type,
      };
    }) as Array<ArtifactId>;

    deleteRatineNews({
      artifactIdList: items,
      portfolioId,
      selectedAll,
    })
      .then(({ data }) => {
        const message =
          ((data as unknown) as { deletePortfolioItems: any }).deletePortfolioItems?.message ||
          t('Success');
        refreshList(true, tableRefreshReasonTypes.DELETED);
        success(message);
      })
      .catch(() => {
        error(t('Unable to delete entities.'));
      })
      .finally(() => setUpdating(false));
  };

  const copyEntity = () => {
    setVariant(selectedAll ? 'copy_all' : 'copy');
    addToPortfolio.toggle();
  };

  const moveEntity = () => {
    setVariant(selectedAll ? 'move_all' : 'move');
    addToPortfolio.toggle();
  };

  /* ******************************************************************** */
  /*                         Custom body renders                          */
  /* ******************************************************************** */

  const nameRender = (name: string, { rowData: [id] }: MUIDataTableMeta) => (
    <LinkButton to={`/announcements/${id}`} label={name.toUpperCase()} />
  );

  const actionRender = (_: unknown, { rowData: [id] }: MUIDataTableMeta) => (
    <ActionsButton onOpen={() => selectSingle(id)} actions={getActions(singleSelect)} />
  );

  /* ******************************************************************** */
  /*                       Table columns definition                       */
  /* ******************************************************************** */

  const initialColumns = useCallback(
    () => [
      {
        name: 'artifactId',
        options: {
          display: false,
        },
      },
      {
        name: 'title',
        label: t('PORTFOLIO.TITLE'),
        options: {
          customBodyRender: nameRender,
        },
      },
      {
        name: 'date',
        label: t('PORTFOLIO.DATE'),
      },
      {
        name: 'moreActions',
        label: ' ',
        options: {
          sort: false,
          customBodyRender: actionRender,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singleSelect.count],
  );

  /* ******************************************************************** */
  /*                             More actions                             */
  /* ******************************************************************** */

  const getActions = (items: SelectedEntities) => [
    {
      text: 'Copy to portfolio',
      handler: copyEntity,
      hidden: items.count === 0,
      disabled: portfolioData.portfolioType === PortfolioType.SAMPLE_PORTFOLIO,
    },
    {
      text: 'Move to portfolio',
      handler: moveEntity,
      hidden: items.count === 0,
      disabled: isDedicated,
    },
    {
      text: 'Delete',
      handler: deleteEntity,
      hidden: items.count === 0,
      color: theme.palette.error.main,
      confirmation: {
        title: 'Deleting rating news',
        text: 'Do you really want to delete that rating news?',
      },
      disabled: isDedicated,
    },
  ];

  /* ******************************************************************** */
  /*                              Table data                              */
  /* ******************************************************************** */
  const { columns, pagination, onSortChange, onTableChange } = useTable({
    initialColumns,
  } as any);

  const { data, loading, refetch } = useFetchPortfolioRatingNews(
    portfolioId,
    pagination.page * pagination.limit,
    pagination.limit,
    pagination.sort && [
      {
        column: pagination.sort.sortColumn,
        direction: pagination.sort.direction,
      },
    ],
  );
  const { ratingNews: portfolioItems } = data || { ratingNews: [] };

  const tableData = useMemo(() => makeRatingNewsTableData(portfolioItems), [portfolioItems]);

  const refreshList = async (refresh: boolean, reason: string) => {
    setUpdating(true);
    if (refetchPortfolio) {
      refetchPortfolio();
    }
    await refetch();
    setShouldRefresh({ value: refresh, reason });
    setUpdating(false);
  };

  /* ******************************************************************** */
  /*                                Render                                */
  /* ******************************************************************** */
  return (
    <Box className={classes.root}>
      <Datatable
        loading={loading || updating}
        columns={columns}
        pagination={pagination}
        data={tableData}
        total={total}
        onSelectedChange={onSelectedChange}
        toolbarData={getActions(multiSelect)}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        selectedAll={selectedAll}
        onSelectAllClick={onSelectAll}
        shouldRefresh={shouldRefresh}
        removeShadow
        showSelectionExtension
      />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        onActionSuccess={refreshList}
        itemsData={currentSelect}
        variant={variant}
        itemType="RATING_NEWS"
        sourcePortfolioId={portfolioId}
      />
      <CreateNewPortfolioModal
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemsData={currentSelect}
        itemType="RATING_NEWS"
        actionVariant={variant}
        sourcePortfolioId={portfolioId}
      />
    </Box>
  );
};
